import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { setProperties } from '@ember/object';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { isBlank } from '@ember/utils';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import findBy from '@nullvoxpopuli/ember-composable-helpers/helpers/find-by';
import pick from '@nullvoxpopuli/ember-composable-helpers/helpers/pick';
import { type DetailedChangeset } from 'ember-changeset/types';
import { dropTask } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import PowerSelect from 'ember-power-select/components/power-select';
import set from 'ember-set-helper/helpers/set';
import SuperTextEditor from 'ember-super-prosemirror/components/super-text-editor';
import { and, eq, not, or } from 'ember-truth-helpers';
import { SUMMARY_ATTRIBUTE_STATES, type SummaryAttributeChangesets } from 'garaje/changesets/summary-attribute';
import LanguageSelector from 'garaje/components/language-selector';
import SummaryPageImageBox from 'garaje/components/page-editor/image-box';
import UiButtonGroup from 'garaje/components/ui/button-group';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import UiButton from 'garaje/components/ui-button';
import UiInputField from 'garaje/components/ui-input-field';
import config from 'garaje/config/environment';
import DoubleConfirmation from 'garaje/helpers/double-confirmation';
import isRtlLocale from 'garaje/helpers/is-rtl-locale';
import type DeviceConfigStaticPageAttributeModel from 'garaje/models/device-config-static-page-attribute';
import type DeviceConfigStaticPageAttributeTranslationModel from 'garaje/models/device-config-static-page-attribute-translation';
import type Flow from 'garaje/models/flow';
import type GlobalFlow from 'garaje/models/global-flow';
import type GlobalSettingBatchModel from 'garaje/models/global-setting-batch';
import type GlobalSummaryPageModel from 'garaje/models/global-summary-page';
import type SummaryAttribute from 'garaje/models/summary-attribute';
import { SummaryAttributeState } from 'garaje/models/summary-attribute';
import type SummaryPageModel from 'garaje/models/summary-page';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import fixVideoUrl from 'garaje/utils/fix-video-url';
import LOCALE_OPTIONS from 'garaje/utils/locale-options';
import manualPropagableHandler from 'garaje/utils/manual-propagable-handler';
import type { ModelAttrs } from 'garaje/utils/type-utils';
import urlBuilder from 'garaje/utils/url-builder';
import { type SingleResponse } from 'jsonapi/response';
import LiquidIf from 'liquid-fire/components/liquid-if';
import invokeMap from 'lodash/invokeMap';
import uniq from 'lodash/uniq';
import { equal, not as notMacro } from 'macro-decorators';
import type ServerError from 'utils/server-error';

export type PageModel =
  | DeviceConfigStaticPageAttributeModel
  | SummaryPageModel
  | GlobalSummaryPageModel
  | SummaryAttribute;
type JoinedPagesModels = DeviceConfigStaticPageAttributeModel & SummaryPageModel & GlobalSummaryPageModel;
type AnySummaryPageModel = SummaryPageModel | GlobalSummaryPageModel;
type PageAttributeProps = Partial<Pick<JoinedPagesModels, 'image' | 'message' | 'videoUrl'>>;

function getErrorValidation(changeset: DetailedChangeset<PageModel>, key: string): string | string[] | undefined {
  return <string | string[] | undefined>changeset.error[key]?.validation;
}

/**
 * The "Sign-in complete" tab edits the SummaryPage itself, while the other tabs
 * each edit the SummaryAttribute matching the corresponding state.
 */
type FinalScreenTab = 'complete' | SummaryAttributeState;

interface FinalScreenTabOption {
  label: string;
  value: FinalScreenTab;
}

const COMPLETE_TAB = 'complete' as const;

const FINAL_SCREEN_TABS: FinalScreenTabOption[] = [
  { label: 'Sign-in complete', value: COMPLETE_TAB },
  { label: 'Pending approval', value: SummaryAttributeState.Review },
  { label: 'Access denied', value: SummaryAttributeState.Denied },
];

interface PageEditorComponentArgs {
  changeset: DetailedChangeset<PageModel>;
  defaultLocale: string;
  flow?: Flow | GlobalFlow;
  globalSettingBatch?: GlobalSettingBatchModel;
  /**
   * Per-state SummaryAttribute changesets for the final-screen tabs. Built
   * upstream (by the route, so its transition guard can see the edits).
   */
  summaryAttributeChangesets?: SummaryAttributeChangesets;
  /**
   * text shown in SettingsPanel description
   */
  headerDescription: string;
  /**
   * text shown in SettingsPanel title
   */
  headerTitle: string;
  isDisabled?: boolean;
  isEditingCustomFinalMessage?: boolean;
  /**
   * whether to show "Email me" button. Note that the "lobbyXp" and "lobbyXpTfvEmail" feature flags are required, in addition to this value being `true`, for the button to be displayed.
   */
  showEmailMeButton: boolean;
  translationLanguagesAvailable: boolean;
  translationEnabledLocales: string[];
  useDirectUploader?: boolean;
  defaultText?: string;
}

interface Locale {
  label: string;
  value: string;
}

const TEXT = { label: 'Text', value: 'message' };
const IMAGE = { label: 'Image', value: 'image' };
const VIDEO = { label: 'Video', value: 'video' };

const OPTIONS = [TEXT, IMAGE, VIDEO];

// "Default" media type, only offered for configurable final screens. Selecting
// it resets the page/attribute to its disabled "none" state. The label depends
// on whether a default message is configured for the page/attribute.
const DEFAULT = { label: 'Default', value: 'none' };
const DEFAULT_NOT_APPLICABLE = { label: 'Default (not applicable)', value: 'none' };

const SUMMARY_PAGE_DEFAULT_PREVIEW = 'Welcome [Name!]';

const EXAMPLE_MARKDOWN = `**This is an example of a summary message**

/**/
/*1*/

Paragraph [Link](https://envoy.com) **this is bold** *this is italic*

> Read, every day, something no one else is reading. Think, every day, something no one else is thinking. Do, every day, something no one else would be silly enough to do. It is bad for the mind to be always part of unanimity.

* one
* two
* three

- uno
- dos
- tres

![image](https://envoy.com/apple-touch-icon.png)`;

export default class PageEditor extends Component<PageEditorComponentArgs> {
  @service declare flashMessages: FlashMessagesService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare store: Store;

  @tracked localeOptions = LOCALE_OPTIONS;
  options = OPTIONS;
  finalScreenTabs = FINAL_SCREEN_TABS;
  @tracked isEditingCustomFinalMessage = false;
  @tracked selectedLocale;
  @tracked imageUrl?: string | null;
  @tracked activeTab: FinalScreenTab = COMPLETE_TAB;

  /**
   * Changesets for the SummaryAttributes edited by the "Pending approval" and
   * "Access denied" tabs, keyed by state. Supplied upstream; empty unless the
   * final-screen tabs are shown.
   */
  summaryAttributeChangesets: SummaryAttributeChangesets = {};

  // The macros read `changeset.kind` so they follow whichever tab is active.
  @equal('changeset.kind', 'message') isText!: boolean;
  @equal('changeset.kind', 'video') isVideo!: boolean;
  @equal('changeset.kind', 'image') isImage!: boolean;
  @equal('changeset.kind', 'none') isNone!: boolean;

  // Image upload is done through the image-box component, which
  // is a file select, a "save" button doesn't make sense in this
  // scenario.
  @notMacro('isImage') showSave!: boolean;

  constructor(owner: unknown, args: PageEditorComponentArgs) {
    super(owner, args);
    this.selectedLocale = this.args.defaultLocale;
    this.isEditingCustomFinalMessage = Boolean(this.args.isEditingCustomFinalMessage);

    this.summaryAttributeChangesets = this.args.summaryAttributeChangesets ?? {};

    this.#setImageUrl();
  }

  get isComplete(): boolean {
    return this.activeTab === COMPLETE_TAB;
  }

  /** DOM id of the active tab, used to label the tabpanel. */
  get activeTabId(): string {
    return `final-screen-tab-${this.activeTab}`;
  }

  /**
   * Type guard for a page that has loaded its summary attributes.
   */
  #hasLoadedSummaryAttributes(page: PageModel): page is AnySummaryPageModel {
    return 'summaryAttributes' in page && page.hasMany('summaryAttributes').value() !== null;
  }

  /**
   * Type guard for a changeset whose content is a summary page or attribute.
   */
  #isSummaryPageOrAttribute(
    changeset: DetailedChangeset<PageModel>,
  ): changeset is DetailedChangeset<AnySummaryPageModel | SummaryAttribute> {
    return !('deviceConfigStaticPageAttributeTranslations' in changeset._content);
  }

  /**
   * Type guard for a changeset whose content is a device-config page.

   */
  #isDeviceConfigChangeset(
    changeset: DetailedChangeset<PageModel>,
  ): changeset is DetailedChangeset<DeviceConfigStaticPageAttributeModel> {
    return 'deviceConfigStaticPageAttributeTranslations' in changeset._content;
  }

  /**
   * Type guard for a changeset whose content is a per-state summary attribute
   * (only attributes carry `state`).
   */
  #isSummaryAttributeChangeset(
    changeset: DetailedChangeset<PageModel>,
  ): changeset is DetailedChangeset<SummaryAttribute> {
    return 'state' in changeset._content;
  }

  get showFinalScreenTabs(): boolean {
    return (
      this.featureFlags.isEnabled('visitors-configurable-final-screens') &&
      this.#hasLoadedSummaryAttributes(this.args.changeset._content)
    );
  }

  /**
   * The default message configured for the active page/attribute (only summary
   * pages/attributes have one). Used to label the "Default" media-type option.
   */
  get defaultMessage(): string | null | undefined {
    const model = this.model;
    return 'defaultMessage' in model ? model.defaultMessage : undefined;
  }

  /**
   * Media-type options for the dropdown. When the final-screen tabs are shown a
   * "Default" option is prepended; its label reflects whether a default message
   * exists for the page/attribute. Selecting it disables the page/attribute.
   */
  get mediaTypeOptions(): { label: string; value: string }[] {
    if (!this.showFinalScreenTabs) {
      return this.options;
    }

    const defaultOption = isBlank(this.defaultMessage) ? DEFAULT_NOT_APPLICABLE : DEFAULT;

    return [defaultOption, ...this.options];
  }

  /**
   * Whether to show the default-message preview.
   */
  get showDefaultPreview(): boolean {
    return this.showFinalScreenTabs && this.isNone;
  }

  /**
   * The default-message preview content.
   */
  get defaultPreview(): string | null | undefined {
    return this.activeTab === COMPLETE_TAB ? SUMMARY_PAGE_DEFAULT_PREVIEW : this.defaultMessage;
  }

  /**
   * The changeset currently being edited. The "Sign-in complete" tab edits the
   * SummaryPage (the changeset passed in); the other tabs edit the matching
   * SummaryAttribute changeset.
   */
  get changeset(): DetailedChangeset<PageModel> {
    if (this.activeTab === COMPLETE_TAB) {
      return this.args.changeset;
    }

    return this.summaryAttributeChangesets[this.activeTab] ?? this.args.changeset;
  }

  /**
   * Every changeset owned by this editor: the SummaryPage and any
   * SummaryAttribute changesets. Used to compute panel-wide dirty state and to
   * roll everything back when the panel is cancelled.
   */
  get allChangesets(): DetailedChangeset<PageModel>[] {
    return [this.args.changeset, ...Object.values(this.summaryAttributeChangesets)];
  }

  /**
   * Whether every changeset (the summary page and each summary attribute) is
   * "none". Drives the closed-panel header button: "Enable" only when nothing is
   * configured, "Edit" when the summary page or any attribute is customized.
   */
  get allNone(): boolean {
    return this.allChangesets.every((changeset) => changeset.kind === 'none');
  }

  get dirtyTranslations(): DeviceConfigStaticPageAttributeTranslationModel[] {
    if (!this.#isDeviceConfigChangeset(this.changeset)) return [];

    return this.changeset.deviceConfigStaticPageAttributeTranslations.content.filter(
      (translation) => translation.hasDirtyAttributes,
    );
  }

  /**
   * Dirty state across every tab. Saving persists all changesets at once, so
   * this drives both the Save button and the cancel confirmation.
   */
  get isDirty(): boolean {
    return this.allChangesets.some((changeset) => changeset.isDirty) || this.dirtyTranslations.length > 0;
  }

  /**
   * The tab label for a changeset, used to point the user at the right tab when
   * a non-active tab is invalid.
   */
  #tabLabelFor(changeset: DetailedChangeset<PageModel>): string {
    const state = SUMMARY_ATTRIBUTE_STATES.find(
      (attributeState) => this.summaryAttributeChangesets[attributeState] === changeset,
    );

    return this.finalScreenTabs.find((tab) => tab.value === (state ?? COMPLETE_TAB))?.label ?? '';
  }

  get model(): PageModel {
    return this.changeset._content;
  }

  get currentTranslationForLocale(): DeviceConfigStaticPageAttributeTranslationModel | undefined {
    if (!this.#isDeviceConfigChangeset(this.changeset)) return undefined;

    return this.changeset.deviceConfigStaticPageAttributeTranslations.content.find(
      (translation) =>
        translation.fromLanguage === this.args.defaultLocale && translation.toLanguage === this.selectedLocale,
    );
  }

  get showEmailMeButton(): boolean {
    return (
      // "Email me" only applies to the SummaryPage itself, not the per-state
      // SummaryAttributes, so it is hidden on the other tabs.
      this.isComplete &&
      this.args.showEmailMeButton &&
      this.featureFlags.isEnabled('lobbyXp') &&
      this.featureFlags.isEnabled('lobbyXpTfvEmail')
    );
  }

  get uploadImageUrl(): string {
    if (this.args.useDirectUploader) {
      return `${config.apiHost}/a/multi-tenancy/api/direct-uploads`;
    }

    return this.isComplete
      ? urlBuilder.v3.summaryPageImageUpload(this.model.id)
      : urlBuilder.v3.summaryAttributeImageUpload(this.model.id);
  }

  get uploadType(): 'final-screen' | 'summary-page' {
    return 'deviceConfigStaticPageAttributeTranslations' in this.model ? 'final-screen' : 'summary-page';
  }

  /**
   * The store model name for the active image upload/delete. The per-state tabs
   * edit a summary attribute; every other case edits the summary page.
   */
  get #imageModelName(): 'summary-page' | 'global-summary-page' | 'summary-attribute' {
    if (!this.isComplete) {
      return 'summary-attribute';
    }

    return this.args.flow?.isGlobal ? 'global-summary-page' : 'summary-page';
  }

  get availableLocales(): Locale[] {
    const { defaultLocale, translationEnabledLocales, flow } = this.args;
    let locales = translationEnabledLocales;

    if (flow?.isGlobal) {
      locales = uniq([...translationEnabledLocales, defaultLocale].flat());
    }

    return this.localeOptions.filter((option) => locales.includes(option.value));
  }

  get messageValue(): string | null | undefined {
    if (this.args.defaultLocale === this.selectedLocale) {
      return this.changeset.message;
    }

    if (!('deviceConfigStaticPageAttributeTranslations' in this.model)) {
      const changeset = <DetailedChangeset<AnySummaryPageModel>>this.changeset;

      const customTranslations = changeset.customTranslations;
      if (
        isBlank(customTranslations['message']) ||
        typeof customTranslations['message'][this.selectedLocale] !== 'string'
      ) {
        return '';
      }
      return changeset.customTranslations['message'][this.selectedLocale];
    } else {
      const { currentTranslationForLocale } = this;
      if (!currentTranslationForLocale) {
        return '';
      }
      return currentTranslationForLocale.value;
    }
  }

  setActiveTab = (tab: FinalScreenTab): void => {
    this.activeTab = tab;
    this.#setImageUrl();
  };

  setKind = (kind: PageModel['kind']): void => {
    const changeset = this.changeset;

    if (kind === 'message' && isBlank(changeset.message)) {
      changeset.message = this.args.defaultText ?? EXAMPLE_MARKDOWN;
    }

    changeset.kind = kind;

    // Clear validation errors by removing them from the changeset
    // This enables the save button when switching message types

    Object.keys(changeset.error || {}).forEach((key) => {
      changeset.removeError(key);
    });
  };

  disableCustomFinalMessage = dropTask(async () => {
    // Disabling resets the SummaryPage and every per-state SummaryAttribute
    // back to "none".
    const changesets = this.allChangesets;

    changesets.forEach((changeset) => {
      changeset.kind = 'none';
      changeset.image = null;
      changeset.videoUrl = null;

      // Only summary attributes reset to their default message; the summary page
      // (and device-config) keep a null message, as before.
      changeset.message = this.#isSummaryAttributeChangeset(changeset) ? changeset._content.defaultMessage : null;
    });

    await this.saveTask.perform(...changesets);

    this.isEditingCustomFinalMessage = false;
  });

  saveTask = dropTask(async (...changesets: PageEditorComponentArgs['changeset'][]) => {
    try {
      await Promise.all(changesets.filter((changeset) => changeset.isDirty).map((changeset) => changeset.save()));

      // save translations if available
      await Promise.all(invokeMap(this.dirtyTranslations, 'save'));
      this.#setImageUrl();

      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e: unknown) {
      let message = 'Server error. Please try again.';

      if ((<ServerError>e).isAdapterError) {
        message = (<ServerError>e).errors.mapBy('detail').join(', ');
      }

      this.flashMessages.showFlash('error', message);
    }
  });

  save = async (): Promise<unknown> => {
    // Only touch the changesets the user edited, so saving one tab never
    // rewrites an attribute the user never opened.
    const editedChangesets = this.allChangesets.filter((changeset) => changeset.isDirty);

    editedChangesets.forEach((changeset) => {
      setProperties(changeset, this.#cleanupProps(changeset));
    });

    await Promise.all(editedChangesets.map((changeset) => changeset.validate()));

    const invalidChangeset = editedChangesets.find((changeset) => changeset.isInvalid);
    if (invalidChangeset) {
      let message = 'Please include a valid URL';
      if (invalidChangeset.kind === TEXT.value) {
        message = 'Please write a message';
      } else if (invalidChangeset.kind === IMAGE.value) {
        message = 'Please add an image';
      }

      // Name the tab so the user can find the error when it's not the active tab.
      if (this.showFinalScreenTabs) {
        message = `${this.#tabLabelFor(invalidChangeset)}: ${message}`;
      }

      this.flashMessages.showAndHideFlash('error', message);
      return;
    }

    if (this.isDirty) {
      const model = await this.saveTask.perform(...editedChangesets);
      // eslint-disable-next-line ember/no-runloop
      next(this, () => {
        this.isEditingCustomFinalMessage = false;
      });
      return model;
    }

    return;
  };

  fixVideoUrl(changeset: DetailedChangeset<PageModel>): void {
    const url = changeset.videoUrl;
    if (url) {
      const fixed = fixVideoUrl(url);

      if (url !== fixed) {
        changeset.videoUrl = fixed;
      }
    }
  }

  setImage = (data?: string | Record<string, unknown>): void => {
    if (this.args.useDirectUploader) {
      this.changeset.image = <string>data;
      void this.save();
    } else {
      /*
          TODO: This code is badly organized, we should move this
          onto the model, and have the model handle image upload.
          We'll need to break up the file uploader first to do this,
          it does too many things right now
        */
      const modelName = this.#imageModelName;
      if (data) this.store.pushPayload(modelName, data);
      const model = this.model;
      const modelProps = { image: model.image, message: model.message, videoUrl: model.videoUrl };

      const changeset = this.changeset;
      setProperties(changeset, modelProps);
      changeset.execute();
      changeset.rollback();

      // Only the summary page participates in global-setting-batch propagation.
      if (this.isComplete) {
        manualPropagableHandler(this.args.globalSettingBatch, 'flow.summaryPage', 'image');
      }

      this.#setImageUrl();
    }
  };

  deleteImage = (): Promise<unknown> => {
    if (this.args.useDirectUploader) {
      this.changeset.image = null;
      return this.save();
    } else {
      const summaryPageOrAttribute = <AnySummaryPageModel>this.model;
      return summaryPageOrAttribute.deleteSummaryPhoto().then((data) => {
        const modelName = this.#imageModelName;
        const normalized = <SingleResponse<AnySummaryPageModel>>this.store.normalize(modelName, data.data);
        // if image is not present then it is not included in the
        // payload. We need to nullify the key.
        //
        normalized.data.attributes.image = null;
        this.store.push(normalized);

        // Only the summary page participates in global-setting-batch propagation.
        if (this.isComplete) {
          manualPropagableHandler(this.args.globalSettingBatch, 'flow.summaryPage', 'image');
        }
      });
    }
  };

  toggleSummaryProperty = dropTask(async (prop: ModelAttrs<PageModel>, value: string) => {
    const changeset = this.changeset;
    changeset[prop] = value;

    await this.saveTask.perform(changeset);
  });

  rollback = (): void => {
    // Roll back the SummaryPage and every SummaryAttribute changeset so
    // cancelling the panel discards edits made on any tab.
    this.allChangesets.forEach((changeset) => changeset.rollback());

    if (!this.#isDeviceConfigChangeset(this.changeset)) return;

    // roll back any edited translations. Deleted any translations that were created and not saved
    this.changeset.deviceConfigStaticPageAttributeTranslations?.invoke('rollbackAttributes');
  };

  messageContentChanged = (value: string): void => {
    if (this.args.defaultLocale === this.selectedLocale) {
      this.changeset.message = value;
      return;
    }

    if (this.#isSummaryPageOrAttribute(this.changeset)) {
      const changeset = this.changeset;
      let customTranslations = Object.assign({}, changeset.customTranslations);
      if (isBlank(customTranslations['message'])) {
        customTranslations = { message: {} };
      }
      customTranslations['message'][this.selectedLocale] = value;
      changeset.customTranslations = customTranslations;
    } else {
      let { currentTranslationForLocale } = this;
      if (!currentTranslationForLocale) {
        currentTranslationForLocale = this.store.createRecord('device-config-static-page-attribute-translation', {
          fromLanguage: this.args.defaultLocale,
          toLanguage: this.selectedLocale,
          translationStatus: 'manually_translated',
          deviceConfigStaticPageAttribute: this.model,
          value,
        });
      }

      currentTranslationForLocale.value = value;
    }
  };

  #setImageUrl() {
    this.imageUrl = this.model.image;
  }

  #cleanupProps(changeset: DetailedChangeset<PageModel>): PageAttributeProps {
    const props: PageAttributeProps = { image: null, message: null, videoUrl: null };

    switch (changeset.kind) {
      case VIDEO.value:
        delete props.videoUrl;
        this.fixVideoUrl(changeset);
        break;
      case TEXT.value:
        delete props.message;
        break;
      case IMAGE.value:
        delete props.image;
        break;
      case DEFAULT.value:
        // Selecting "Default" disables this page/attribute (image/videoUrl/message
        // already default to null). Only summary attributes reset their message to
        // the configured default.
        if (this.#isSummaryAttributeChangeset(changeset)) {
          props.message = changeset._content.defaultMessage;
        }
        break;
    }

    return props;
  }

  <template>
    <SettingsPanel
      @isOpen={{this.isEditingCustomFinalMessage}}
      @onCancel={{DoubleConfirmation
        dirty=this.isDirty
        close=(fn (mut this.isEditingCustomFinalMessage) false)
        rollback=this.rollback
      }}
      data-test-summary-page
      ...attributes
      as |panel|
    >
      <panel.header
        @showCancel={{not @isDisabled}}
        @iconUri="/assets/images/features/customfinalmessage.svg"
        @title={{@headerTitle}}
        @description={{@headerDescription}}
        as |header|
      >
        <header.controls>
          {{#if panel.isOpen}}
            {{#if (or this.showFinalScreenTabs (and (not this.isNone) this.showSave))}}
              <button
                class="btn btn--small btn--brand"
                disabled={{or this.changeset.isInvalid (not this.isDirty) @isDisabled this.saveTask.isRunning}}
                {{on "click" this.save}}
                data-test-save-button
              >
                {{if this.saveTask.isIdle "Save" "Saving"}}
              </button>
            {{/if}}
          {{else}}
            <UiButton
              {{on "click" (fn (mut this.isEditingCustomFinalMessage) true)}}
              class="btn btn--small {{if (and this.allNone @isDisabled) 'btn--link !text-carbon-50' 'btn--brand'}}"
              disabled={{and this.allNone @isDisabled}}
              data-test-edit-custom-message
            >
              {{#if this.allNone}}
                {{if @isDisabled "Not enabled" "Enable"}}
              {{else}}
                {{if @isDisabled "View" "Edit"}}
              {{/if}}
            </UiButton>
          {{/if}}
        </header.controls>
      </panel.header>
      <panel.body>
        {{#if this.showFinalScreenTabs}}
          <div class="tokenized-form mt-4">
            <UiButtonGroup @segmented={{true}} role="tablist" aria-label="Final screen" data-test-final-screen-tabs>
              {{#each this.finalScreenTabs as |tab|}}
                <UiButton
                  role="tab"
                  id="final-screen-tab-{{tab.value}}"
                  aria-selected={{eq this.activeTab tab.value}}
                  aria-controls="final-screen-tabpanel"
                  class="btn btn--gemOutlineFill btn--small {{if (eq this.activeTab tab.value) 'active font-bold'}}"
                  {{on "click" (fn this.setActiveTab tab.value)}}
                  data-test-final-screen-tab={{tab.value}}
                >
                  {{tab.label}}
                </UiButton>
              {{/each}}
            </UiButtonGroup>
          </div>
        {{/if}}
        <div
          class="tokenized-form mt-8 flow-root"
          role={{if this.showFinalScreenTabs "tabpanel"}}
          id={{if this.showFinalScreenTabs "final-screen-tabpanel"}}
          aria-labelledby={{if this.showFinalScreenTabs this.activeTabId}}
        >
          <div class="w-4/12 mb-8">
            <label class="label-reset text-sm text-carbon-50 font-medium {{unless @isDisabled 'cursor-pointer'}}">
              Message Type
            </label>
            <div data-test-choose-media-type>
              <PowerSelect
                @options={{this.mediaTypeOptions}}
                @selected={{findBy "value" this.changeset.kind this.mediaTypeOptions}}
                @searchEnabled={{false}}
                @placeholder="Choose a media type"
                @onChange={{pick "value" this.setKind}}
                @disabled={{@isDisabled}}
                as |option|
              >
                {{option.label}}
              </PowerSelect>
            </div>
          </div>
          <div class="lg:w-9/12">
            <LiquidIf @predicate={{this.isText}}>
              {{#if @translationLanguagesAvailable}}
                <LanguageSelector
                  class="flex justify-end"
                  @languages={{this.availableLocales}}
                  @onChange={{set this "selectedLocale"}}
                  @value={{this.selectedLocale}}
                  @isDisabled={{@isDisabled}}
                />
              {{/if}}

              <SuperTextEditor
                {{! @glint-expect-error }}
                class="ember-prose-mirror"
                @isDisabled={{@isDisabled}}
                @isRtl={{isRtlLocale this.selectedLocale}}
                @onContentChange={{this.messageContentChanged}}
                @value={{this.messageValue}}
                @uploadType={{this.uploadType}}
                @uploadId={{this.model.id}}
                data-test-message-content
              />
            </LiquidIf>
            <LiquidIf @predicate={{this.isImage}} class="-mx-4">
              <SummaryPageImageBox
                @model={{this.model}}
                @changeset={{this.changeset}}
                @uploadImageUrl={{this.uploadImageUrl}}
                @useDirectUploader={{@useDirectUploader}}
                @deleteImage={{this.deleteImage}}
                @setImage={{this.setImage}}
                @imgSrc={{this.imageUrl}}
                @isDisabled={{@isDisabled}}
              />
            </LiquidIf>
            <LiquidIf @predicate={{this.isVideo}}>
              <UiInputField
                @label="Video URL"
                @errors={{getErrorValidation this.changeset "videoUrl"}}
                @type="url"
                @disabled={{@isDisabled}}
                data-test-field-for="video"
                as |Field|
              >
                <Field.label />
                <Field.input
                  @value={{this.changeset.videoUrl}}
                  @update={{set this.changeset "videoUrl"}}
                  autocomplete="off"
                  spellcheck="false"
                />
              </UiInputField>
              <div class="flow-root">
                <div class="float-left box-border w-8/12">
                  <p class="mb-2 text-sm">
                    Link to a video hosted on YouTube or Vimeo.
                  </p>
                  <p class="text-sm m-0">
                    <b>
                      Tip:
                    </b>
                    You can also host a file on your own SSL-encrypted server. iOS requires
                    <a href="https://www.ssllabs.com/ssltest/" target="_blank" rel="noopener">
                      SSL Grade A
                    </a>
                    to play remote video files. File types accepted: MP4, MOV, M4V.
                  </p>
                </div>
              </div>
            </LiquidIf>
            <LiquidIf @predicate={{this.showDefaultPreview}}>
              {{#if this.defaultPreview}}
                <p class="text-sm text-carbon-50 font-medium mb-2">
                  Preview default message
                </p>
                <div class="bg-carbon-5 rounded-lg p-6 whitespace-pre-line flex" data-test-default-preview>
                  {{this.defaultPreview}}
                </div>
              {{else}}
                <p class="text-sm text-carbon-50 font-medium" data-test-no-default-preview>
                  No preview default message
                </p>
              {{/if}}
            </LiquidIf>
          </div>
          <LiquidIf @predicate={{not this.isNone}}>
            <div class="lg:w-9/12">
              <div class="border-t mt-8 py-8 flex">
                {{#if this.showEmailMeButton}}
                  <div class="flex-auto min-w-0 min-h-0 pr-4">
                    <h4>
                      "Email me this info" button
                    </h4>

                    <p class="m-0">
                      Allow visitors to email themselves a copy of this
                      <span style="text-transform: lowercase;">
                        {{#let (findBy "value" this.changeset.kind this.options) as |option|}}
                          {{if option option.label}}
                        {{/let}}
                      </span>
                      .
                    </p>
                  </div>
                  <label class="toggle">
                    <input
                      data-test-allow-email
                      type="checkbox"
                      {{! @glint-expect-error }}
                      checked={{this.changeset.allowEmail}}
                      disabled={{or this.toggleSummaryProperty.isRunning @isDisabled}}
                      {{on "click" (perform this.toggleSummaryProperty "allowEmail" value="target.checked")}}
                    />
                    <span></span>
                  </label>
                {{/if}}
              </div>
            </div>
          </LiquidIf>
        </div>
        <LiquidIf @predicate={{and (not this.allNone) (not @isDisabled)}}>
          <button
            class="btn btn--small btn--outline float-right m-2"
            tabindex="-1"
            {{on "click" this.disableCustomFinalMessage.perform}}
          >
            {{if this.disableCustomFinalMessage.isIdle "Disable" "Disabling"}}
          </button>
        </LiquidIf>
      </panel.body>
    </SettingsPanel>
  </template>
}
